/**
 * Admin: service accounts, access groups, sync settings. The Entra session
 * cookie is sent by apiFetch.
 */
import { apiFetch } from "./apiClient";
import type {
  AuthGroup,
  EntityColumnsData,
  ServiceAccount,
  ServiceAccountCreatedData,
  SyncSettings,
} from "../types/api";

/* ---- Service accounts ---- */

export function listServices(): Promise<ServiceAccount[]> {
  return apiFetch<ServiceAccount[]>("/api/v1/admin/services");
}

export function getService(id: number | string): Promise<ServiceAccount> {
  return apiFetch<ServiceAccount>(`/api/v1/admin/services/${id}`);
}

export interface CreateServiceBody {
  name: string;
  groupIds: number[];
}

export function createService(body: CreateServiceBody): Promise<ServiceAccountCreatedData> {
  return apiFetch<ServiceAccountCreatedData>("/api/v1/admin/services", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export interface PatchServiceBody {
  name?: string;
  isActive?: boolean;
  groupIds?: number[];
}

export function patchService(id: number | string, body: PatchServiceBody): Promise<ServiceAccount> {
  return apiFetch<ServiceAccount>(`/api/v1/admin/services/${id}`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

export function disableService(id: number | string): Promise<ServiceAccount> {
  return apiFetch<ServiceAccount>(`/api/v1/admin/services/${id}`, { method: "DELETE" });
}

export function refreshServiceKey(id: number | string): Promise<{ apiKey: string }> {
  return apiFetch<{ apiKey: string }>(`/api/v1/admin/services/${id}/refresh-key`, { method: "POST" });
}

export function revokeServiceKey(id: number | string): Promise<ServiceAccount> {
  return apiFetch<ServiceAccount>(`/api/v1/admin/services/${id}/revoke-key`, { method: "POST" });
}

/* ---- Access groups ---- */

export function listGroups(): Promise<AuthGroup[]> {
  return apiFetch<AuthGroup[]>("/api/v1/admin/groups");
}

export function getGroup(id: number | string): Promise<AuthGroup> {
  return apiFetch<AuthGroup>(`/api/v1/admin/groups/${id}`);
}

export interface CreateGroupBody {
  name: string;
  entity: string;
  columns: string[];
}

export function createGroup(body: CreateGroupBody): Promise<AuthGroup> {
  return apiFetch<AuthGroup>("/api/v1/admin/groups", { method: "POST", body: JSON.stringify(body) });
}

export interface PatchGroupBody {
  name?: string;
  columns?: string[];
}

export function patchGroup(id: number | string, body: PatchGroupBody): Promise<AuthGroup> {
  return apiFetch<AuthGroup>(`/api/v1/admin/groups/${id}`, { method: "PATCH", body: JSON.stringify(body) });
}

export function deleteGroup(id: number | string): Promise<void> {
  return apiFetch<void>(`/api/v1/admin/groups/${id}`, { method: "DELETE" });
}

export function getGroupServices(id: number | string): Promise<ServiceAccount[]> {
  return apiFetch<ServiceAccount[]>(`/api/v1/admin/groups/${id}/services`);
}

export async function getEntityColumns(entity: string): Promise<string[]> {
  const data = await apiFetch<EntityColumnsData>(`/api/v1/admin/entities/${entity}/columns`);
  return data.columns;
}

/* ---- Sync settings ---- */

export function getSyncSettings(): Promise<SyncSettings> {
  return apiFetch<SyncSettings>("/api/v1/admin/settings/sync");
}

/**
 * Admin-editable fields only, matching `app/schemas/sync_settings.py::SyncSettingsPatch`
 * exactly. `apiDefaultPageSize` / `apiMaxPageSize` are intentionally absent -
 * the backend does not accept them on PATCH (env-sourced, GET-only), even
 * though the frontend spec doc lists them as editable; the backend schema
 * wins.
 */
export interface PatchSyncSettingsBody {
  syncEnabled?: boolean;
  scheduleKind?: SyncSettings["scheduleKind"];
  scheduleTime?: string;
  weekdays?: number[] | null;
  monthDay?: number | null;
  timesheetSyncWindowDays?: number;
  timesheetSyncOverlapDays?: number;
  timesheetSyncLookbackDays?: number;
  lastModifiedOverlapHours?: number;
}

export function patchSyncSettings(body: PatchSyncSettingsBody): Promise<SyncSettings> {
  return apiFetch<SyncSettings>("/api/v1/admin/settings/sync", {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}